import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { EntityHistoryPanel } from "../components/EntityHistoryPanel";

type PartyType = "Supplier" | "Customer" | "Regulator" | "Partner" | "Other";

const ALL_TYPES: PartyType[] = ["Supplier", "Customer", "Regulator", "Partner", "Other"];

interface Party {
  uuid: string;
  partyName: string;
  partyTypes: PartyType[];
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  websiteUrl: string | null;
  relationshipSummary: string | null;
  theirRequirements: string | null;
  ourRequirements: string | null;
  isComplianceObligation: boolean;
  linkedNetworkConnectionUuid: string | null;
  notes: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const formatDateUk = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

export function PartyDetail() {
  const { slug, uuid } = useParams<{ slug: string; uuid: string }>();
  const navigate = useNavigate();
  const { activeOrganisation } = useAuth();
  const canWrite =
    activeOrganisation?.role === "Admin" || activeOrganisation?.role === "Contributor";

  const [party, setParty] = useState<Party | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const [partyName, setPartyName] = useState("");
  const [partyTypes, setPartyTypes] = useState<PartyType[]>([]);
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [relationshipSummary, setRelationshipSummary] = useState("");
  const [theirRequirements, setTheirRequirements] = useState("");
  const [ourRequirements, setOurRequirements] = useState("");
  const [isComplianceObligation, setIsComplianceObligation] = useState(false);
  const [notes, setNotes] = useState("");
  const [isActive, setIsActive] = useState(true);

  const refresh = useCallback(async () => {
    if (!uuid) return;
    try {
      const res = await api<{ party: Party }>(`/interested-parties/${uuid}`);
      setParty(res.party);
      setPartyName(res.party.partyName);
      setPartyTypes(res.party.partyTypes);
      setContactName(res.party.contactName ?? "");
      setContactEmail(res.party.contactEmail ?? "");
      setContactPhone(res.party.contactPhone ?? "");
      setWebsiteUrl(res.party.websiteUrl ?? "");
      setRelationshipSummary(res.party.relationshipSummary ?? "");
      setTheirRequirements(res.party.theirRequirements ?? "");
      setOurRequirements(res.party.ourRequirements ?? "");
      setIsComplianceObligation(res.party.isComplianceObligation);
      setNotes(res.party.notes ?? "");
      setIsActive(res.party.isActive);
    } catch (err) {
      setErrors(formatApiError(err));
    }
  }, [uuid]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const toggleType = (t: PartyType) => {
    setPartyTypes((curr) => (curr.includes(t) ? curr.filter((x) => x !== t) : [...curr, t]));
  };

  const onSave = async (event: FormEvent) => {
    event.preventDefault();
    if (!party) return;
    setBusy(true);
    setErrors([]);
    try {
      await api(`/interested-parties/${party.uuid}`, {
        method: "PATCH",
        body: {
          partyName,
          partyTypes,
          contactName: contactName.trim() || null,
          contactEmail: contactEmail.trim() || null,
          contactPhone: contactPhone.trim() || null,
          websiteUrl: websiteUrl.trim() || null,
          relationshipSummary: relationshipSummary.trim() || null,
          theirRequirements: theirRequirements.trim() || null,
          ourRequirements: ourRequirements.trim() || null,
          isComplianceObligation,
          notes: notes.trim() || null,
          isActive,
        },
      });
      await refresh();
    } catch (err) {
      setErrors(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    if (!party) return;
    setBusy(true);
    setErrors([]);
    try {
      await api(`/interested-parties/${party.uuid}`, { method: "DELETE" });
      navigate(`/o/${slug}/parties`);
    } catch (err) {
      setErrors(formatApiError(err));
      setBusy(false);
      setConfirmingDelete(false);
    }
  };

  if (errors.length > 0 && !party) {
    return (
      <div
        role="alert"
        className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200"
      >
        {errors[0]}
      </div>
    );
  }

  if (!party) {
    return <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>;
  }

  const readOnly = !canWrite;

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <Link
          to={`/o/${slug}/parties`}
          className="text-xs font-medium text-brand-600 hover:text-brand-700 dark:text-brand-300"
        >
          ← Interested parties
        </Link>
        <h1 className="mt-1 text-2xl font-semibold text-slate-900 dark:text-slate-100">
          {party.partyName}
        </h1>
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          Created {formatDateUk(party.createdAt)} · last updated {formatDateUk(party.updatedAt)}
        </p>
      </div>

      {errors.length > 0 ? (
        <div
          id="party-errors"
          role="alert"
          className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200"
        >
          {errors[0]}
        </div>
      ) : null}

      <form onSubmit={onSave} className="space-y-4" noValidate>
        <div>
          <label
            htmlFor="party-name"
            className="block text-sm font-medium text-slate-700 dark:text-slate-200"
          >
            Party name
          </label>
          <input
            id="party-name"
            type="text"
            required
            disabled={readOnly}
            value={partyName}
            onChange={(e) => setPartyName(e.target.value)}
            aria-invalid={errors.length > 0 || undefined}
            aria-describedby={errors.length > 0 ? "party-errors" : undefined}
            className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500 disabled:opacity-60 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
          />
        </div>

        <fieldset disabled={readOnly}>
          <legend className="text-sm font-medium text-slate-700 dark:text-slate-200">
            Type (one or more)
          </legend>
          <div className="mt-2 flex flex-wrap gap-2">
            {ALL_TYPES.map((t) => {
              const checked = partyTypes.includes(t);
              return (
                <label
                  key={t}
                  className={`cursor-pointer rounded-md border px-3 py-1.5 text-sm ${
                    checked
                      ? "border-brand-500 bg-brand-50 text-brand-900 dark:bg-brand-500/10 dark:text-brand-100"
                      : "border-slate-300 bg-white text-slate-700 hover:border-slate-400 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleType(t)}
                    className="sr-only"
                  />
                  {t}
                </label>
              );
            })}
          </div>
        </fieldset>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            id="party-contact-name"
            label="Contact name"
            value={contactName}
            onChange={setContactName}
            disabled={readOnly}
          />
          <Field
            id="party-contact-email"
            label="Contact email"
            type="email"
            value={contactEmail}
            onChange={setContactEmail}
            disabled={readOnly}
          />
          <Field
            id="party-contact-phone"
            label="Contact phone"
            value={contactPhone}
            onChange={setContactPhone}
            disabled={readOnly}
          />
          <Field
            id="party-website"
            label="Website"
            type="url"
            value={websiteUrl}
            onChange={setWebsiteUrl}
            disabled={readOnly}
          />
        </div>

        <TextArea
          id="party-relationship"
          label="Relationship summary"
          hint="What does this party do for you, or you for them?"
          value={relationshipSummary}
          onChange={setRelationshipSummary}
          disabled={readOnly}
        />
        <TextArea
          id="party-their-reqs"
          label="Their needs and expectations of us"
          hint="ISO §4.2: what does this party expect from your organisation?"
          value={theirRequirements}
          onChange={setTheirRequirements}
          disabled={readOnly}
        />
        <TextArea
          id="party-our-reqs"
          label="Our requirements of them"
          hint="What do you require from this party?"
          value={ourRequirements}
          onChange={setOurRequirements}
          disabled={readOnly}
        />

        <label className="flex cursor-pointer items-start gap-2">
          <input
            type="checkbox"
            disabled={readOnly}
            checked={isComplianceObligation}
            onChange={(e) => setIsComplianceObligation(e.target.checked)}
            className="mt-0.5"
          />
          <span className="text-sm text-slate-700 dark:text-slate-200">
            This party imposes a compliance obligation
            <span className="mt-0.5 block text-xs text-slate-500 dark:text-slate-400">
              Feeds the legal/regulatory register.
            </span>
          </span>
        </label>

        <TextArea
          id="party-notes"
          label="Notes"
          hint="Anything else worth recording. Internal use only."
          value={notes}
          onChange={setNotes}
          disabled={readOnly}
        />

        <label className="flex cursor-pointer items-center gap-2">
          <input
            type="checkbox"
            disabled={readOnly}
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
          />
          <span className="text-sm text-slate-700 dark:text-slate-200">
            Active
            <span className="ml-2 text-xs text-slate-500 dark:text-slate-400">
              Untick to retain the record but hide it from the default list.
            </span>
          </span>
        </label>

        {!readOnly ? (
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={busy || partyTypes.length === 0 || partyName.trim().length === 0}
              className="rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {busy ? "Saving…" : "Save changes"}
            </button>
          </div>
        ) : null}
      </form>

      <EntityHistoryPanel entityType="interested_parties" entityUuid={party.uuid} />

      {!readOnly ? (
        <section className="border-t border-slate-200 pt-4 dark:border-slate-700">
          {confirmingDelete ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-950/40">
              <p className="text-sm font-medium text-red-900 dark:text-red-100">
                Delete &quot;{party.partyName}&quot;?
              </p>
              <p className="mt-1 text-xs text-red-700 dark:text-red-200">
                Soft-deletes the record. Audit trail entries naming this party are preserved.
              </p>
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  onClick={() => void onDelete()}
                  disabled={busy}
                  className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-60"
                >
                  {busy ? "Deleting…" : "Yes, delete"}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmingDelete(false)}
                  className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmingDelete(true)}
              disabled={busy}
              className="rounded-md border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-60 dark:border-red-800 dark:bg-slate-800 dark:text-red-200 dark:hover:bg-red-950/40"
            >
              Delete party
            </button>
          )}
        </section>
      ) : null}
    </div>
  );
}

function Field({
  id,
  label,
  value,
  onChange,
  type = "text",
  disabled,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: "text" | "email" | "url";
  disabled?: boolean;
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-slate-700 dark:text-slate-200">
        {label}
      </label>
      <input
        id={id}
        type={type}
        disabled={disabled}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500 disabled:opacity-60 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
      />
    </div>
  );
}

function TextArea({
  id,
  label,
  hint,
  value,
  onChange,
  disabled,
}: {
  id: string;
  label: string;
  hint: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-slate-700 dark:text-slate-200">
        {label}
      </label>
      <textarea
        id={id}
        rows={3}
        disabled={disabled}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500 disabled:opacity-60 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
      />
      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{hint}</p>
    </div>
  );
}
